import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { validateEmail, validatePassword } from '../../utils/validation';
import { isNetworkError, handleNetworkError } from '../../utils/api/networkErrors';

interface SignupMetadata {
  first_name: string;
  last_name: string;
  onboarding_data?: Record<string, any>;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

async function attemptSignup(email: string, password: string, metadata: SignupMetadata, retryCount = 0) {
  try {
    // Add delay for retries
    if (retryCount > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, retryCount - 1)));
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: metadata.first_name,
          last_name: metadata.last_name,
          full_name: `${metadata.first_name} ${metadata.last_name}`,
          ...metadata.onboarding_data
        }
      }
    });

    if (error) {
      // Retry on network errors or database errors
      if ((isNetworkError(error) || error.message?.includes('Database error')) && 
          retryCount < MAX_RETRIES) {
        console.log(`Retrying signup attempt ${retryCount + 1}/${MAX_RETRIES}...`);
        return attemptSignup(email, password, metadata, retryCount + 1);
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
}

export async function signUp(email: string, password: string, metadata: SignupMetadata) {
  try {
    // Validate required fields
    if (!email || !password || !metadata.first_name || !metadata.last_name) {
      return { error: 'All fields are required' };
    }

    // Validate email and password
    const emailError = validateEmail(email);
    if (emailError) {
      return { error: emailError };
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      return { error: passwordError };
    }

    // Attempt signup with retries
    const { data, error } = await attemptSignup(email, password, metadata);

    if (error) {
      if (error.message?.includes('already exists') || error.status === 422) {
        return { existingUser: true };
      }
      return { error: error.message };
    }

    if (!data?.user) {
      return { error: 'Failed to create account' };
    }

    // Wait for profile creation
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Verify profile was created
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('id')
      .eq('id', data.user.id)
      .single();

    if (profileError || !profile) {
      console.warn('Profile verification failed:', profileError);
    }

    return { user: data.user, session: data.session };
  } catch (error) {
    console.error('Signup error:', error);
    if (isNetworkError(error)) {
      handleNetworkError(error, () => signUp(email, password, metadata));
    }
    return { error: error instanceof Error ? error.message : 'Failed to create account' };
  }
}